<template>
  <b-modal
    ref="modal"
    v-model="isVisible"
    size="md"
    centered
    @hidden="resetModal"
    hide-footer
    body-class="p-0"
  >
    <template #modal-header="{ close }">
      <div class="modal-header-custom">
        <h5 class="modal-title">
          <i class="las la-chart-bar"></i>
          Credit Model Assessment
        </h5>
        <button type="button" class="close-btn" @click="close()" aria-label="Close">
          <i class="las la-times"></i>
        </button>
      </div>
    </template>

    <div class="assessment-modal-body">
      <div v-if="processing" class="modal-loading">
        <div class="loading-spinner"></div>
        <p>Processing assessment...</p>
      </div>
      
      <div v-else>
        <div class="form-section">
          <label class="input-label">
            <i class="las la-percent"></i>
            Payer Score Threshold
          </label>
          <p class="input-description">
            Enter the payer score threshold to use for the automated assessment analysis.
          </p>
          <input
            v-model.number="payerScoreThreshold"
            type="number"
            class="threshold-input"
            placeholder="Enter payer score threshold"
            min="0"
            max="100"
            step="0.01"
            :disabled="processing"
          />
          <div v-if="error" class="error-message">
            <i class="las la-exclamation-circle"></i>
            {{ error }}
          </div>
        </div>

        <div class="modal-actions">
          <button 
            class="btn-cancel" 
            @click="closeModal"
            :disabled="processing"
          >
            Cancel
          </button>
          <button 
            class="btn-submit" 
            @click="submitAssessment"
            :disabled="!isValid || processing"
          >
            <i v-if="processing" class="las la-spinner la-spin"></i>
            <i v-else class="las la-arrow-right"></i>
            {{ processing ? 'Processing...' : 'Continue to Report' }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";

export default {
  name: 'CreditModelAssessmentModal',
  props: {
    candidateId: {
      type: String,
      required: true
    },
    fileId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isVisible: false,
      payerScoreThreshold: null,
      processing: false,
      error: null
    }
  },
  computed: {
    isValid() {
      return this.payerScoreThreshold !== null && 
             this.payerScoreThreshold !== '' && 
             !isNaN(this.payerScoreThreshold) &&
             this.payerScoreThreshold >= 0 &&
             this.payerScoreThreshold <= 100;
    }
  },
  methods: {
    show() {
      this.isVisible = true;
      this.resetModal();
    },
    
    closeModal() {
      this.$refs.modal.hide();
    },
    
    resetModal() {
      this.payerScoreThreshold = null;
      this.processing = false;
      this.error = null;
    },
    
    async submitAssessment() {
      if (!this.isValid) {
        this.error = 'Please enter a valid payer score threshold (0-100)';
        return;
      }

      if (!this.fileId) {
        this.error = 'File ID is missing. Please try again.';
        return;
      }

      this.processing = true;
      this.error = null;

      try {
        const payload = {
          payer_score_threshold: parseFloat(this.payerScoreThreshold),
          file_id: this.fileId
        };

        await ChatbotService.analyzeAutomatedAssessment(this.candidateId, payload);
        
        // Close modal
        this.closeModal();
        
        // Redirect to report details
        this.$router.push({
          name: 'chatbot-report-details',
          params: {
            candidateId: this.candidateId,
            fileId: this.fileId
          },
          query: {
            reportType: 'credit_model'
          }
        });
      } catch (err) {
        console.error('Error submitting assessment:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to process assessment. Please try again.';
      } finally {
        this.processing = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
  
  .modal-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
    font-family: $brand-font-medium;
    
    i {
      color: $color-primary;
      font-size: 1.5rem;
    }
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f3f4f6;
      color: #2d3748;
    }
  }
}

.assessment-modal-body {
  padding: 1.5rem;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid $color-primary;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  p {
    color: #6c757d;
    font-size: 1rem;
    margin: 0;
  }
}

.form-section {
  margin-bottom: 1.5rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
  font-family: $brand-font-medium;
  
  i {
    color: $color-primary;
    font-size: 1.25rem;
  }
}

.input-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.threshold-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: $brand-font-regular;
  color: #2d3748;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: $color-primary;
    box-shadow: 0 0 0 3px rgba($color-primary, 0.1);
  }
  
  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  
  i {
    font-size: 1rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.btn-cancel,
.btn-submit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: $brand-font-medium;
  
  i {
    font-size: 1rem;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-cancel {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #e2e8f0;
  
  &:hover:not(:disabled) {
    background: #e9ecef;
  }
}

.btn-submit {
  background: $color-primary;
  color: $color-secondary;
  
  &:hover:not(:disabled) {
    background: darken($color-primary, 10%);
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
